import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, CalendarDays, MessageSquare, Inbox, CarFront, ArrowRight } from "lucide-react";
import api from "../../lib/api";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "-");

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((res) => setStats(res.data)).catch(() => {});
  }, []);

  const cards = stats
    ? [
        { label: "New Bookings", value: stats.new_bookings, icon: CalendarCheck, testid: "stat-new-bookings" },
        { label: "Total Bookings", value: stats.total_bookings, icon: CalendarDays, testid: "stat-total-bookings" },
        { label: "New Enquiries", value: stats.new_enquiries, icon: MessageSquare, testid: "stat-new-enquiries" },
        { label: "Total Enquiries", value: stats.total_enquiries, icon: Inbox, testid: "stat-total-enquiries" },
        { label: "Cars For Sale", value: stats.cars_for_sale, icon: CarFront, testid: "stat-cars-for-sale" },
      ]
    : [];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-heading font-extrabold text-white text-2xl tracking-tight">Dashboard</h1>
      <p className="text-pale/50 text-sm mt-1">Overview of bookings and enquiries.</p>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-7">
        {!stats
          ? [...Array(5)].map((_, i) => (
              <div key={i} className="bg-navy border border-deep rounded-lg p-5">
                <div className="h-20 animate-pulse bg-deep/40 rounded" />
              </div>
            ))
          : cards.map((c) => (
              <div key={c.testid} data-testid={c.testid} className="bg-navy border border-deep rounded-lg p-5">
                <c.icon size={20} className="text-sky" />
                <p className="font-mono font-semibold text-white text-3xl mt-3">{c.value}</p>
                <p className="text-pale/50 text-xs font-heading font-bold uppercase tracking-wider mt-1">{c.label}</p>
              </div>
            ))}
      </div>

      <div className="mt-8 bg-navy border border-deep rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-deep">
          <h2 className="font-heading font-bold text-white text-sm tracking-wide uppercase">Recent Bookings</h2>
          <Link to="/admin/bookings" data-testid="view-all-bookings" className="inline-flex items-center gap-1.5 text-xs font-heading font-bold text-sky hover:text-white tracking-wide transition-colors">
            VIEW ALL <ArrowRight size={13} />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="recent-bookings-table">
            <thead>
              <tr className="text-left text-pale/40 text-xs uppercase tracking-wider">
                <th className="px-5 py-3 font-heading">Customer</th>
                <th className="px-5 py-3 font-heading">Vehicle</th>
                <th className="px-5 py-3 font-heading">Service</th>
                <th className="px-5 py-3 font-heading">Date</th>
                <th className="px-5 py-3 font-heading">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-deep/60">
              {(stats?.recent_bookings || []).map((b) => (
                <tr key={b.id} className="text-pale/80">
                  <td className="px-5 py-3.5 font-medium text-white">{b.name}</td>
                  <td className="px-5 py-3.5"><span className="font-mono text-xs bg-deep/70 px-2 py-1 rounded">{b.vehicle_reg || "-"}</span></td>
                  <td className="px-5 py-3.5">{b.service}</td>
                  <td className="px-5 py-3.5 text-pale/60">{b.preferred_date || fmtDate(b.created_at)}</td>
                  <td className="px-5 py-3.5">
                    <span className={`status-chip ${b.status === "NEW" ? "bg-sky/15 text-sky border border-sky/30" : "bg-deep/60 text-pale/70 border border-deep"}`}>{b.status}</span>
                  </td>
                </tr>
              ))}
              {stats && stats.recent_bookings.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-pale/40">No bookings yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
